import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authFetch } from "@/lib/api";

const BACKEND_URL = process.env.NODE_ENV === "development"
  ? (process.env.REACT_APP_BACKEND_URL || "")
  : "";

const SUPPORTED_LANGUAGES = [
  { code: "EN", name: "English", native_name: "English" },
  { code: "TH", name: "Thai", native_name: "ไทย" },
];

const LanguageContext = createContext({
  language: "EN",
  languages: SUPPORTED_LANGUAGES,
  loading: true,
  setDisplayLanguage: () => {},
  fetchDisplayLanguage: () => {},
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState("EN");
  const [languages, setLanguages] = useState(SUPPORTED_LANGUAGES);
  const [loading, setLoading] = useState(true);

  const fetchDisplayLanguage = useCallback(async () => {
    try {
      const [languageRes, optionsRes] = await Promise.allSettled([
        authFetch(`${BACKEND_URL}/api/display-language`),
        authFetch(`${BACKEND_URL}/api/display-language/options`),
      ]);

      if (languageRes.status === "fulfilled" && languageRes.value.ok) {
        const data = await languageRes.value.json();
        setLanguageState(data.display_language || "EN");
      }

      if (optionsRes.status === "fulfilled" && optionsRes.value.ok) {
        const data = await optionsRes.value.json();
        setLanguages(data.languages || SUPPORTED_LANGUAGES);
      }
    } catch {
      // Keep English as the safe default if language preference cannot load.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDisplayLanguage();
  }, [fetchDisplayLanguage]);

  const setDisplayLanguage = useCallback(async (code) => {
    const next = String(code || "EN").toUpperCase();
    try {
      const res = await authFetch(`${BACKEND_URL}/api/display-language`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_language: next }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      setLanguageState(data.display_language || next);
      return true;
    } catch {
      return false;
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ language, languages, loading, setDisplayLanguage, fetchDisplayLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
